/**
 * Dealer List Component
 *
 * @package
 */

import { useState, useEffect, useRef } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';
import Icon from './Icon';

const DealerList = ( { vehicleBrand, selectedDealer, onDealerSelect, onOpenModal } ) => {
	const [ dealerships, setDealerships ] = useState( [] );
	const [ filteredDealerships, setFilteredDealerships ] = useState( [] );
	const [ searchQuery ] = useState( '' );
	const [ loading, setLoading ] = useState( true );
	const itemRefs = useRef( {} );

	useEffect( () => {
		if ( ! vehicleBrand ) {
			setDealerships( [] );
			setFilteredDealerships( [] );
			setLoading( false );
			return;
		}

		loadDealerships( vehicleBrand );
	}, [ vehicleBrand ] );

	useEffect( () => {
		if ( ! searchQuery.trim() ) {
			setFilteredDealerships( dealerships );
			return;
		}

		const query = searchQuery.toLowerCase().trim();
		const filtered = dealerships.filter( dealership => {
			const title = dealership.title?.rendered || '';
			return title.toLowerCase().includes( query );
		} );
		setFilteredDealerships( filtered );
	}, [ searchQuery, dealerships ] );

	const loadDealerships = async brandId => {
		setLoading( true );
		try {
			const response = await apiFetch( {
				path: `/wp/v2/dealerships?brand=${ brandId }&per_page=100`,
			} );

			if ( Array.isArray( response ) ) {
				setDealerships( response );
				setFilteredDealerships( response );
			}
		} catch ( error ) {
			// eslint-disable-next-line no-console
			console.error( 'Failed to load dealerships:', error );
			setDealerships( [] );
		} finally {
			setLoading( false );
		}
	};

	// Scroll to selected dealer item.
	useEffect( () => {
		if ( selectedDealer && itemRefs.current[ selectedDealer ] ) {
			// Check if user prefers reduced motion
			const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
			itemRefs.current[ selectedDealer ].scrollIntoView( {
				behavior: prefersReducedMotion ? 'auto' : 'smooth',
				block: 'nearest',
			} );
		}
	}, [ selectedDealer ] );

	const handleItemClick = ( dealershipId, e ) => {
		// Don't trigger if clicking the button.
		if ( e.target.closest( '.configurator-dealer-item-button' ) ) {
			return;
		}
		if ( onDealerSelect ) {
			onDealerSelect( dealershipId );
		}
	};

	const handleButtonClick = ( dealershipId, e ) => {
		e.stopPropagation();
		if ( onDealerSelect ) {
			onDealerSelect( dealershipId );
		}
		if ( onOpenModal ) {
			onOpenModal( dealershipId );
		}
	};

	if ( loading ) {
		return (
			<div className="configurator-dealer-list loading" role="status" aria-live="polite">
				<span className="screen-reader-text">{ __( 'Loading dealerships…', 'at-vehicle-configurator' ) }</span>
				<div className="configurator-dealer-list-scroll">
					{ [ 1, 2, 3, 4, 5 ].map( i => (
						<div key={ i } className="configurator-dealer-item configurator-dealer-list-item--skeleton" aria-hidden="true">
							<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '60%', height: '20px', display: 'block', marginBottom: '8px' } } />
							<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '95%', height: '14px', display: 'block', marginBottom: '4px' } } />
							<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '50%', height: '14px', display: 'block', marginBottom: '4px' } } />
							<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '45%', height: '14px', display: 'block', marginBottom: '12px' } } />
							<span className="configurator-skeleton configurator-skeleton--button" style={ { width: '140px', height: '44px' } } />
						</div>
					) ) }
				</div>
			</div>
		);
	}

	if ( dealerships.length === 0 ) {
		return <div className="configurator-dealer-list empty">{ __( 'No dealerships found for this brand.', 'at-vehicle-configurator' ) }</div>;
	}

	return (
		<div className="configurator-dealer-list">
			{ /* Search input - removed for MVP */ }

			{ /* <div className="configurator-dealer-list-search">
				<label htmlFor="dealer-search-input" className="configurator-dealer-list-search-label">{ __( 'Search:', 'at-vehicle-configurator' ) }</label>
				<div className="configurator-dealer-list-search-wrapper">
					<input
						id="dealer-search-input"
						type="search"
						className="configurator-dealer-list-search-input"
						placeholder={ __( 'Type to search…', 'at-vehicle-configurator' ) }
						value={ searchQuery }
						onChange={ e => setSearchQuery( e.target.value ) }
						aria-label={ __( 'Search dealerships', 'at-vehicle-configurator' ) }
					/>
					<button
						type="button"
						className="configurator-dealer-list-search-button"
						aria-label={ __( 'Search', 'at-vehicle-configurator' ) }
					>
						<span className="configurator-dealer-list-search-icon">🔍</span>
					</button>
				</div>
			</div> */ }
			<div className="configurator-dealer-list-scroll">
				{ filteredDealerships.length === 0 ? (
					<div className="configurator-dealer-list-empty">
						{ __( 'No dealerships match your search.', 'at-vehicle-configurator' ) }
					</div>
				) : (
					filteredDealerships.map( dealership => {
						const acf = dealership.acf || {};
						const location = acf.location || {};

						return (
							<div
								key={ dealership.id }
								ref={ el => {
									itemRefs.current[ dealership.id ] = el;
								} }
								className={ `configurator-dealer-item ${ selectedDealer === dealership.id ? 'selected' : '' }` }
								role="button"
								tabIndex={ 0 }
								onClick={ e => handleItemClick( dealership.id, e ) }
								onKeyDown={ e => {
									if ( e.key === 'Enter' || e.key === ' ' ) {
										e.preventDefault();
										handleItemClick( dealership.id, e );
									}
								} }
								aria-label={ __( 'Select dealership', 'at-vehicle-configurator' ) + ': ' + ( dealership.title?.rendered || __( 'Dealership', 'at-vehicle-configurator' ) ) }
							>
								<h3>{ dealership.title?.rendered || __( 'Dealership', 'at-vehicle-configurator' ) }</h3>
								{ location.address && <p>{ location.address }</p> }
								{ acf.phone && <p>{ __( 'Phone:', 'at-vehicle-configurator' ) } { acf.phone }</p> }
								{ acf.email && <p>{ __( 'Email:', 'at-vehicle-configurator' ) } { acf.email }</p> }
								<button
									type="button"
									className="configurator-dealer-item-button"
									onClick={ e => handleButtonClick( dealership.id, e ) }
								>
									{ __( 'Contact Dealer', 'at-vehicle-configurator' ) }
									<Icon name="arrow-right" width={ 17 } height={ 15 } style={ { marginLeft: '0.5rem' } } />
								</button>
							</div>
						);
					} )
				) }
			</div>
		</div>
	);
};

export default DealerList;
